"use client"

import { useUIBuilderLocaleStore } from "@/(admin)/modules/ui-builder/store/useUIBuilderLocaleStore"
import { routing } from "@/i18n/routing"
import { fetchPostByCategories } from "@/services/postServices"
import {
  NotificationListRenderProps,
  PostData,
  UseNotificationListResult,
} from "@/types/types"
import { useParams } from "next/navigation"
import { useEffect, useMemo, useState } from "react"

export const useNotificationList = (
  props: NotificationListRenderProps,
  componentId?: string,
): UseNotificationListResult => {
  const { viewAllLink, category } = props
  const params = useParams()
  const storeLocale = useUIBuilderLocaleStore(state => state.locale)
  const locale = (params?.locale as string) || storeLocale || "vi"

  const [posts, setPosts] = useState<PostData[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const id = useMemo(
    () => componentId || props.id || `notification-list-${Math.random().toString(36).substr(2, 9)}`,
    [componentId, props.id],
  )

  const finalViewAllLink = useMemo(() => {
    if (viewAllLink) return viewAllLink
    if (!category?.slug) return undefined

    const categoryPath = `/category/${category.slug}`
    return locale === routing.defaultLocale ? categoryPath : `/${locale}${categoryPath}`
  }, [viewAllLink, category?.slug, locale])

  useEffect(() => {
    if (!category?.categoryId) {
      setPosts([])
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)

    fetchPostByCategories([String(category.categoryId)], {
      page: "1",
      pageSize: "4",
      locale,
    })
      .then(response => {
        if (cancelled) return
        setPosts((response?.data as PostData[]) || [])
      })
      .catch(err => {
        if (cancelled) return
        console.error("Error fetching notifications:", err)
        setError(err instanceof Error ? err.message : "Unknown error")
        setPosts([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => { cancelled = true }
  }, [category?.categoryId, locale])

  return {
    posts,
    loading,
    error,
    id,
    postIds: [],
    finalViewAllLink,
    locale,
  }
}
